import { z } from 'zod/v4';

import { eventTypeSchema, messageNamespaceSchema } from './events/base';

export interface ConnectedDapp {
  grantId: string;
  dappId: string;
  dappName: string;
  dappOrigin: string;
  expiresAt: string;
}

/* ----- LoggingConfig ----- */

const _knownSources = [
  'sdk',
  'channel',
  'popup-manager',
  'popup',
  'messenger',
  'app',
  'test',
] as const;

// Allows us to add more in the future without fucking up the SDK handshake
export const loggingSourceSchema = z.union([z.enum(_knownSources), z.string()]);
export type LoggingSource = z.infer<typeof loggingSourceSchema>;

export const logLevelSchema = z
  .enum(['debug', 'info', 'warn', 'error', 'none'])
  .default('info');
export type LogLevel = z.infer<typeof logLevelSchema>;

export const loggingConfigSchema = z
  .object({
    globalDisable: z.boolean().optional(),
    disableOutgoing: z.boolean().optional(),
    disableIncoming: z.boolean().optional(),
    disabledMethods: z.array(z.string()).optional(),
    disabledNamespaces: z.array(messageNamespaceSchema).optional(),
    disabledSources: z.array(loggingSourceSchema).optional(),
    disabledEventTypes: z.array(eventTypeSchema).optional(),
    logLevel: logLevelSchema,
    debugLevel: z.number(),
  })
  .strict();
export type LoggingConfig = z.infer<typeof loggingConfigSchema>;

/* ----- Timeouts & monitors ----- */
export const systemTimeoutsSchema = z
  .object({
    notificationWait: z.number().optional(),
    handshakeTimeout: z.number().optional(),
  })
  .strict();
export type SystemTimeouts = z.infer<typeof systemTimeoutsSchema>;

export const systemMonitorsSchema = z
  .object({
    popupMonitorInterval: z.number().optional(),
  })
  .strict();
export type SystemMonitors = z.infer<typeof systemMonitorsSchema>;

export const evmChainIdSchema = z.templateLiteral([
  z.literal('0x'),
  z.string(),
]);
export type EvmChainId = z.infer<typeof evmChainIdSchema>;

export const popupFeaturesSchema = z
  .object({
    static: z.string(),
    positionPercent: z.object({
      right: z.templateLiteral([z.number(), z.literal('%')]),
      top: z.templateLiteral([z.number(), z.literal('%')]),
    }),
  })
  .strict();
export type PopupFeatures = z.infer<typeof popupFeaturesSchema>;

/* ----- SystemConfig ----- */
export const systemConfigSchema = z
  .object({
    enableCookies: z.boolean(),
    rpID: z.string(),
    origin: z.string(),
    logging: loggingConfigSchema,
    supportedEvmChains: z.array(evmChainIdSchema),
    popupFeatures: popupFeaturesSchema,
    timeouts: systemTimeoutsSchema,
    monitoring: systemMonitorsSchema.optional(),
    closePopupAfterTimeoutMs: z.number().optional(),
  })
  // In case we add more config in the future that only pertains to the channel
  .catchall(z.any());
export type SystemConfig = z.infer<typeof systemConfigSchema>;

export const authorizationOptionsRequest = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('sign'),
      txnRequestId: z.string().optional(),
    })
    .describe('Used to create + authorize a sign grant'),
  z
    .object({
      type: z.literal('account'),
    })
    .describe('Used to authorize an account grant'),
]);

export type AuthorizationOptionsRequest = z.infer<
  typeof authorizationOptionsRequest
>;

export const authorizationOptionsResponse = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('requires-authorization'),
    authorizationId: z.string(),
    webautnOptions: z.custom<PublicKeyCredentialRequestOptionsJSON>(),
  }),
  z.object({
    status: z.literal('authorized'),
  }),
]);

export type AuthorizationOptionsResponse = z.infer<
  typeof authorizationOptionsResponse
>;

export const dappSessionSelectorSchema = z.object({
  dappId: z.string(),
  userId: z.string(),
  globalSessionId: z.string(),
  // TODO: remove once the migration to 0.0.33+ is complete
  deviceId: z.string().nullish(),
});

export type DappSessionSelector = z.infer<typeof dappSessionSelectorSchema>;
